import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
} from '@mui/material';
import { Person, CheckCircle, HourglassEmpty, HowToReg } from '@mui/icons-material';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { deepPurple, green, grey, orange, purple } from '@mui/material/colors';
import { getConsentForms } from '../services/consentFormService';

const statusOptions = [
  { value: 'approved', label: 'Approved', color: green[600], icon: <CheckCircle /> },
  { value: 'pending', label: 'Pending', color: orange[600], icon: <HourglassEmpty /> },
];

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function consentStatus(f) {
  return f.approved_at ? 'approved' : 'pending';
}

export default function ConsentFormsList() {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('submitted_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleCloseError = () => setError('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await getConsentForms();
      if (result.success) setForms(result.data || []);
      else setError(result.message || 'Failed to load consent forms');
      setLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    return forms.filter((f) => {
      const status = consentStatus(f);
      const q = search.trim().toLowerCase();
      const hay = `${f.user_id} ${f.user_name || ''} ${f.admin_approved_by_name || ''} ${status}`.toLowerCase();
      const matchesSearch = !q || hay.includes(q);
      const matchesStatus = !statusFilter || status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [forms, search, statusFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let aVal;
      let bVal;
      if (sortBy === 'submitted_at') {
        aVal = a.submitted_at || a.created_at ? new Date(a.submitted_at || a.created_at) : 0;
        bVal = b.submitted_at || b.created_at ? new Date(b.submitted_at || b.created_at) : 0;
      } else if (sortBy === 'user_name') {
        aVal = (a.user_name || '').toLowerCase();
        bVal = (b.user_name || '').toLowerCase();
      } else if (sortBy === 'status') {
        aVal = consentStatus(a);
        bVal = consentStatus(b);
      } else {
        aVal = a.user_id;
        bVal = b.user_id;
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortBy, sortOrder]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container
        maxWidth="lg"
        sx={{
          pt: 10,
          mt: 4,
          mb: 6,
          flex: 1,
          pb: 8,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: deepPurple[800] }}>
              Submitted consent forms
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
              These are participant consent records already captured in the portal (for example rental or program
              agreements). Each row shows the participant, which staff member approved the record, when it was
              submitted, and whether approval is complete.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'nowrap', alignItems: 'center' }}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ mb: 0, flex: 1 }}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 180, flex: '0 0 220px' }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Snackbar
            open={!!error}
            autoHideDuration={4000}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: deepPurple[500] }} />
            </Box>
          ) : sorted.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No consent forms match your filters.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: deepPurple[50] }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>User ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'user_name'}
                        direction={sortBy === 'user_name' ? sortOrder : 'asc'}
                        onClick={() => {
                          setSortBy('user_name');
                          setSortOrder(sortBy === 'user_name' && sortOrder === 'asc' ? 'desc' : 'asc');
                        }}
                      >
                        User name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Admin approved by</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'submitted_at'}
                        direction={sortBy === 'submitted_at' ? sortOrder : 'asc'}
                        onClick={() => {
                          setSortBy('submitted_at');
                          setSortOrder(sortBy === 'submitted_at' && sortOrder === 'asc' ? 'desc' : 'asc');
                        }}
                      >
                        Submitted
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortOrder : 'asc'}
                        onClick={() => {
                          setSortBy('status');
                          setSortOrder(sortBy === 'status' && sortOrder === 'asc' ? 'desc' : 'asc');
                        }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((f) => {
                    const st = consentStatus(f);
                    const opt = statusOptions.find((o) => o.value === st);
                    return (
                      <TableRow key={f.id} hover>
                        <TableCell>
                          <Chip
                            label={String(f.user_id)}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: purple[100], color: purple[800] }}>
                              <Person />
                            </Avatar>
                            <Box>
                              <Typography fontWeight="medium">{f.user_name || 'Unknown'}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Participant
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: deepPurple[100], color: deepPurple[800] }}>
                              <HowToReg />
                            </Avatar>
                            <Box>
                              <Typography fontWeight="medium">{f.admin_approved_by_name || '—'}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Approving staff
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDateTime(f.submitted_at || f.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={opt?.icon}
                            label={opt?.label || st}
                            sx={{
                              backgroundColor: `${opt?.color || grey[300]}10`,
                              color: opt?.color || grey[800],
                              fontWeight: 600,
                              border: `1px solid ${opt?.color || grey[800]}30`,
                              px: 1,
                              '& .MuiChip-icon': {
                                color: opt?.color || grey[800],
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={sorted.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          )}
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
}
